// WHAT AN EXPORT IS FOR, which decides who may be in it and which columns it
// may carry.
//
// exportRule.ts answers a question about one person: given what they consented
// to, what may be said about them. This file answers the question one layer up,
// and it is the question the roadmap says is the dangerous one — an export that
// flattens away who agreed to what "invites a mailing list built from people who
// declined one", and the file that is a marketing list "should refuse to include
// those who said no rather than trusting the reader".
//
// THE FAILURE THIS EXISTS TO MAKE IMPOSSIBLE, stated as the sequence of
// perfectly reasonable steps that produces it. A client asks for their attendee
// list. Somebody runs the export, which is one button and produces every
// counted registration with a consent map on each row. The file lands in a
// marketing team's inbox. Nobody there reads a `consent` column, because the
// file arrived in answer to "send me the list" and every row in it looks like a
// row that is there to be used. The consent was carried faithfully, per person,
// on every row, and it was still flattened — by the reader, at the last step,
// which is the only step this code cannot reach.
//
// So carrying consent per row is necessary and is not sufficient. The artefact
// itself has to know what it is for, and the one that is for contacting people
// has to be structurally incapable of containing somebody who declined. Not
// filtered: incapable. A filter is a line of code somebody can move, and the
// whole history of this file's neighbours is failures that were one line away
// from being right in the direction nothing notices.
//
// ---------------------------------------------------------------------------
// THREE PURPOSES, AND WHY IT IS NOT ONE WITH FLAGS.
//
// A single export with `include_marketing_only: true` beside it is the same
// mechanism with a worse shape. The flag defaults to something; the default is
// the permissive one, because the permissive one is the one that does not break
// existing callers; and from then on the safe artefact is the one you have to
// remember to ask for. Naming the purposes makes the choice explicit at the
// call site and makes it a value that can be written into the audit record, so
// `report_access` can distinguish somebody taking a participation report from
// somebody taking a mailing list. Those are very different acts and a table
// that records them identically cannot answer the question a client will
// actually ask.
//
//   participation_report  Everything, gated per person by exportRule.ts. This
//                         is what the export has always produced and the
//                         default, so nothing about today's behaviour changes
//                         by omission. It requires no consent to *include*
//                         somebody, because a row for a person who consented to
//                         nothing is a row with nothing identifying on it and a
//                         count that stays true — case 3 of the rule, and the
//                         argument for it is in exportRule.ts.
//
//   attendance_only       The genuine minimisation case. Somebody who needs to
//                         know how many people came and how many came back
//                         needs no names, and today the only way to answer them
//                         is to hand over the whole file. A purpose whose
//                         column set contains no name, no address and no
//                         telephone number cannot leak one however it is
//                         misread.
//
//   marketing_list        The dangerous artefact, made safe by being the only
//                         one that can never contain a person who declined.
//                         Requires a live consent for client_data_transfer,
//                         contact AND marketing, and a person missing any of
//                         the three is not withheld, redacted or flagged: they
//                         are absent.
//
// ---------------------------------------------------------------------------
// WHY ABSENT RATHER THAN WITHHELD, WHICH CONTRADICTS CASE 3 OF THE RULE AND IS
// MEANT TO.
//
// exportRule.ts argues at length that dropping a row is wrong: it silently
// changes the totals, the export stops reconciling against the operator view,
// and "a shrinking file with no explanation is indistinguishable from data
// loss". Every word of that is right about a participation report, which is a
// record of what happened.
//
// A marketing list is not a record of what happened. It is an instruction —
// these are the people you may contact — and the two artefacts fail in opposite
// directions. A participation report that omits somebody understates a day. A
// marketing list that includes somebody who declined is an unlawful mailing,
// and it is worse than that, because the person it happens to already said no
// once and has no reason to believe it will not happen again.
//
// So the reconciliation argument is answered rather than ignored: the manifest
// carries how many people were omitted and how many failed each requirement, so
// the file still adds up against the participation report of the same
// activation. What it does not do is carry a row per omitted person, because a
// row saying "this person declined marketing" in a file whose whole purpose is
// marketing is a row somebody will mail.
//
// ---------------------------------------------------------------------------
// AND THE CASE THE OPT-IN SURFACE TAUGHT US, which is the one this would have
// got wrong.
//
// optIn.ts records a finding that applies here with full force: there is no
// consent event for a refusal, so a purpose with no event at all is ambiguous
// between "nobody was asked" and "everybody refused", and those are opposite
// facts. An activation whose configuration bundle never offered `marketing`
// therefore produces an empty marketing list, and an empty marketing list reads
// as "nobody here wants to hear from us" when the truth may be that nobody was
// asked.
//
// Refusing to produce the file at all was considered, on access.ts's reasoning
// that an empty report and a refused one look identical. It is rejected here,
// and the difference is that access.ts is about a reader who cannot be told why
// their report is empty, whereas this file *can* be told: the manifest names
// every required purpose for which this activation holds no consent event of any
// kind, with both readings spelled out. A refusal would also make an ordinary
// case — an activation that genuinely offered no marketing consent — into an
// error somebody has to work around, and a control people work around is worse
// than a sentence they read.

import type { ConsentPurpose, ConsentStates } from "./exportRule.js";

/**
 * Every column the export can produce, in the order a file carries them.
 *
 * One list, so that the manifest, the audit record, the SQL and the
 * minimisation guard cannot disagree about what a column is called. Order is
 * canonical rather than taken from whatever the caller asked for, because a
 * client diffing this week's file against last week's should not see columns
 * move because somebody typed the list differently.
 */
export const ALL_EXPORT_COLUMNS = Object.freeze([
  "attendee_ref",
  "registered_at",
  "format",
  "display_name",
  "contact_email",
  "deliver_to_email",
  "phone",
  "visits",
  // The provenance trio, placed after the details and before the consent
  // account, because they qualify the details: what wrote the row, how well the
  // address was established, and when it was proved if it ever was. See
  // COLUMN_DEFINITIONS below for the words that travel with them.
  "capture_channel",
  "identity_assurance",
  "email_verified_at",
  "consent",
  "personal_data",
  "withheld_fields",
  "personal_data_note",
  // Written survey answers, carried by the survey_free_text purpose alone
  // (decision C6, 12 Oct). Last, and outside every other template's column
  // set: a participation report does not quote anybody.
  "free_text",
  // What the prize wheel gave this person, carried by the wheel_results
  // purpose alone (156, decision C10). After free_text, and outside every
  // other template, because it is about the wheel and not about the visit.
  "wheel",
] as const);

export type ExportColumn = (typeof ALL_EXPORT_COLUMNS)[number];

/**
 * The columns that identify a person, which is the set minimisation is about.
 *
 * `registered_at` is not here and it is a judgement rather than an oversight: a
 * timestamp is personal data about an identified person and it is also the only
 * thing that makes a row locatable in an earlier file. It is minimisable like
 * everything else below; it is not in this list because this list is what the
 * consent rule gates, and exportRule.ts gates the four contact slots.
 */
export const PERSONAL_EXPORT_COLUMNS = Object.freeze([
  "display_name",
  "contact_email",
  "deliver_to_email",
  "phone",
] as const);

/**
 * The columns no purpose and no caller may remove.
 *
 * THIS IS THE HALF OF ITEM 1 THAT IS EASY TO LOSE. A file whose `consent`
 * column has been minimised away is precisely the flattened artefact the
 * roadmap forbids, and it would be produced by a caller doing the right thing —
 * asking for less — with no way for anybody downstream to notice, because a
 * file with fewer columns looks more careful rather than less.
 *
 * So minimisation is asymmetric on purpose. It may remove what a person told
 * us. It may never remove the account of what they permitted, or the key that
 * lets the client find the row in an earlier file and act on a withdrawal.
 */
export const UNMINIMISABLE_COLUMNS = Object.freeze([
  "attendee_ref",
  "consent",
  "personal_data",
  "withheld_fields",
  "personal_data_note",
  // THE PROVENANCE TRIO IS MANDATORY FOR THE SAME REASON THE CONSENT ACCOUNT IS.
  //
  // These three do not describe the person, they describe how much the record
  // can be relied on, and a file that has dropped them looks exactly like a file
  // where every address was proved. That is the worst possible failure mode: it
  // is silent, it flatters the data, and the client acts on it. A file missing a
  // phone number is obviously missing a phone number.
  //
  // They are also not personal data in the sense minimisation is about — nobody
  // is identified by learning that a registration came off a tablet — so
  // removing them buys no privacy and costs the reader the ability to tell two
  // very different records apart.
  "capture_channel",
  "identity_assurance",
  "email_verified_at",
] as const);

/**
 * What each column means, in words, carried in the manifest of every file.
 *
 * The three provenance columns are closed enums, and a closed enum in a
 * spreadsheet is jargon unless its values are defined next to it.
 * `self_asserted` reads like a criticism and `mailbox_verified` reads like a
 * guarantee, and neither is quite right: the definitions are what stop a client
 * inventing the difference for themselves.
 *
 * In the manifest rather than in a wiki, because a file gets forwarded and a
 * wiki does not go with it. The person who has to interpret this in eight
 * months is very likely not the person who asked for it.
 */
export const COLUMN_DEFINITIONS: Readonly<Partial<Record<ExportColumn, string>>> = Object.freeze({
  capture_channel:
    "What wrote this row. stand_tablet: an enrolled device on the stand, whether a crew member " +
    "held it or the attendee did. self_serve_web: the attendee's own phone, through the " +
    "registration page reached by scanning the stand's code. onsite_web: a hall browser opened " +
    "at the onsite address, not an enrolled tablet and not a sofa phone.",
  identity_assurance:
    "How well the email address was established. self_asserted: somebody typed it and nothing " +
    "was ever sent to it, so it may not exist and may not belong to this person. " +
    "mailbox_verified: a code or link sent to that address was returned, so somebody could read " +
    "that mailbox at that moment. Neither value says anything about whether the rest of the " +
    "record is accurate.",
  free_text:
    "This person's answers to the free-text survey questions, as a list of the question asked and what " +
    "they wrote, in the order the form asked them. A question they left blank is not listed. Their own " +
    "words, unedited: nothing here was checked or corrected.",
  wheel:
    "This person's spin of the prize wheel: when (UTC), which lane (onsite at a hall browser, web on their " +
    "own phone, or empty for a spin recorded before lanes were), the segment it landed on, whether that was " +
    "a prize, the claim code for a prize and when it stopped being good, and when it was collected at the " +
    "stand. A blank has no code. One spin per person.",
  email_verified_at:
    "When the address was proved, in UTC. Empty where it was never proved, which is every " +
    "self_asserted row. Not the registration time: see registered_at for that.",
});

export type ExportPurposeId =
  | "participation_report"
  | "attendance_only"
  | "marketing_list"
  | "survey_free_text"
  | "wheel_results";

export type ExportPurposeSpec = {
  id: ExportPurposeId;
  /**
   * The template id this purpose files itself under.
   *
   * Distinct per purpose so that `report_access.detail` distinguishes the three
   * acts. `participation_report` keeps the id the export has always used, so
   * that audit rows written before this file existed remain comparable with the
   * ones written after it: renaming it would make every historical row look
   * like a template nobody can find.
   */
  template_id: string;
  /**
   * Consent purposes every included person must hold live. Empty means
   * inclusion is not gated and the per-person rule decides what may be said.
   */
  requires: readonly ConsentPurpose[];
  /** The most this purpose may carry. A caller may ask for fewer and never for more. */
  columns: readonly ExportColumn[];
  /** What this file is, for whoever opens it without having read any of this. */
  says: string;
  /** What the holder is now responsible for, which is the roadmap's fourth question. */
  responsibility: string;
};

const PARTICIPATION_REPORT: ExportPurposeSpec = {
  id: "participation_report",
  template_id: "xp1.attendees.minimal",
  requires: [],
  // Every column but the written survey answers, which only the purpose named
  // for them carries. A record of who took part is not a place to quote them.
  columns: Object.freeze(ALL_EXPORT_COLUMNS.filter((column) => column !== "free_text" && column !== "wheel")),
  says:
    "A record of who took part in this activation. Every capture that counts has a row, including people who consented to nothing. Their row carries no personal details and says so, because the visit happened and the count has to stay true. This file is not permission to contact anybody: read the consent column on each row before doing anything with it.",
  responsibility:
    "From the moment this file is downloaded, the client holds a copy of personal data that Nova cannot reach, cannot amend and cannot delete. Acting on a withdrawal recorded in a later export is the client's to do.",
};

const ATTENDANCE_ONLY: ExportPurposeSpec = {
  id: "attendance_only",
  template_id: "xp1.attendees.attendance",
  requires: [],
  columns: Object.freeze([
    "attendee_ref",
    "registered_at",
    "visits",
    // Mandatory in every file, by UNMINIMISABLE_COLUMNS above. Listed here as
    // well rather than left to the guard, because a purpose spec is meant to be
    // readable as the complete description of its file, and a column that
    // appears only because something else forces it in is a column nobody
    // reviewing this list would know was coming.
    "capture_channel",
    "identity_assurance",
    "email_verified_at",
    "consent",
    "personal_data",
    "withheld_fields",
    "personal_data_note",
  ] as const),
  says:
    "How many people took part and how often each of them came back, with nobody named. The attendee reference is a pseudonym scoped to this one activation and cannot be joined to any other. There is no name, no email address and no telephone number in this file, and no column of this template can carry one.",
  responsibility:
    "The attendee reference is pseudonymous rather than anonymous, so this is still personal data and still the client's to look after. It cannot be used to contact anybody, which is the point of asking for it instead of the full report.",
};

const MARKETING_LIST: ExportPurposeSpec = {
  id: "marketing_list",
  template_id: "xp1.attendees.marketing",
  /**
   * Three consents, and each one is doing separate work.
   *
   * `client_data_transfer` is permission for the client to hold anything about
   * this person at all. `contact` is permission to be contacted. `marketing` is
   * permission to be contacted about something other than what they were
   * standing there for — and it is the one the export has never looked at,
   * which is the gap this purpose closes. Today a participation report hands
   * over an email address on the strength of a `contact` consent whose wording
   * is about this event, and nothing in the file stops that address being used
   * for a campaign. The consent state was on the row. Nobody read it.
   */
  requires: Object.freeze(["client_data_transfer", "contact", "marketing"] as const),
  /**
   * `deliver_to_email` is deliberately absent, and it is the narrowest decision
   * in this file.
   *
   * The schema calls it a routing override: a guest's photograph going to the
   * inviting attendee's address. That address belongs to somebody who may never
   * have been asked anything, and mailing a campaign to it would be marketing
   * to a person on the strength of another person's consent. It stays in the
   * participation report, where it is what it says it is.
   */
  columns: Object.freeze([
    "attendee_ref",
    "display_name",
    "contact_email",
    "phone",
    // Mandatory in every file, by UNMINIMISABLE_COLUMNS above. Listed here as
    // well rather than left to the guard, because a purpose spec is meant to be
    // readable as the complete description of its file, and a column that
    // appears only because something else forces it in is a column nobody
    // reviewing this list would know was coming.
    "capture_channel",
    "identity_assurance",
    "email_verified_at",
    "consent",
    "personal_data",
    "withheld_fields",
    "personal_data_note",
  ] as const),
  says:
    "People who may be contacted for marketing about this activation, and nobody else. Anybody who declined, withdrew, or was never asked for any one of the three consents this file requires is not in it: not redacted, not flagged, absent. So this file needs no filtering before it is used, and filtering it further can only ever be right.",
  responsibility:
    "This list is true as at the moment it was generated and stops being true afterwards. A withdrawal recorded after this file was produced is not in it, so the participation report's withdrawal records are the instruction to stop and the client is the only party that can act on them.",
};

/**
 * The written answers to this show's free-text survey questions, under a purpose
 * named for them (decision C6, 12 Oct).
 *
 * THIS REVERSES WHAT survey.ts SAID AND IT IS MEANT TO. The survey report counts
 * written answers and never quotes them, on the ground that a written answer is
 * personal data, and until this ruling nothing produced the words at all. They
 * can now leave the building, and only here: as a file asked for by this name,
 * recorded in report_access like every other export, by somebody the export
 * already admits (an Admin, or an Editor or a Viewer with their switch on, each
 * on a session that proved a factor) and for a client with a processing
 * agreement on file. The survey report itself still counts and does not quote.
 *
 * WHO IS IN IT. Somebody who holds a live consent to their data being shared
 * with the client, and nobody else: absent rather than redacted, on the
 * marketing list's reasoning, because a row whose whole content is withheld is
 * a row with nothing in it. Their name and addresses are not in this file: the
 * attendee reference finds the row in a participation report of the same show
 * for whoever is entitled to that.
 */
const SURVEY_FREE_TEXT: ExportPurposeSpec = {
  id: "survey_free_text",
  template_id: "xp1.attendees.survey_free_text",
  requires: Object.freeze(["client_data_transfer"] as const),
  columns: Object.freeze([
    "attendee_ref",
    "registered_at",
    "capture_channel",
    "identity_assurance",
    "email_verified_at",
    "consent",
    "personal_data",
    "withheld_fields",
    "personal_data_note",
    "free_text",
  ] as const),
  says:
    "What people wrote in answer to this show's free-text survey questions, word for word, for the people who agreed to their data being shared with the client and nobody else. There is no name, no email address and no telephone number in this file. A written answer is personal data whatever it says, because it is attached to a person: somebody may have written their own name, or somebody else's.",
  responsibility:
    "From the moment this file is downloaded the client holds people's own words, which Nova cannot reach, amend or delete. Read them for what they were asked for. A person who later withdraws their consent is in a later participation report's withdrawal records, and acting on that is the client's to do.",
};

/**
 * What the prize wheel gave away, person by person, as a file (156, decision
 * C10): the Results page's Export.
 *
 * WHO IS IN IT. Everybody who spun, and nobody who did not: the file is the
 * record of the wheel, and its row count is the Results page's count of spins.
 * Inclusion is not gated on a consent, on the participation report's
 * reasoning: a spin happened whether or not the person agreed to anything, and
 * a file that dropped the blanks or the unconsented would make the odds look
 * other than they were. The name and the address are gated per person by the
 * export rule exactly as they are there, so a person who did not agree to
 * their details being shared is a row with a spin and nothing identifying.
 */
const WHEEL_RESULTS: ExportPurposeSpec = {
  id: "wheel_results",
  template_id: "xp1.attendees.wheel_results",
  requires: [],
  columns: Object.freeze([
    "attendee_ref",
    "display_name",
    "contact_email",
    "capture_channel",
    "identity_assurance",
    "email_verified_at",
    "consent",
    "personal_data",
    "withheld_fields",
    "personal_data_note",
    "wheel",
  ] as const),
  says:
    "Every spin of this activation's prize wheel, one row per person who spun: what it landed on, the claim code for a prize, and whether it was collected. Blanks are in it, because they are the evidence the odds behaved. A person's name and address appear only where they agreed to their details being shared with the client; otherwise the row carries the spin and nothing identifying. This file is not permission to contact anybody.",
  responsibility:
    "From the moment this file is downloaded, the client holds a copy of personal data that Nova cannot reach, cannot amend and cannot delete. The claim codes in it are what a person quotes at the stand to collect a prize, so it should be kept by whoever hands prizes over and nobody else.",
};

/**
 * Looked up rather than defaulted, and there is no fallback on purpose.
 *
 * A typo in `purpose` must not quietly produce the biggest file this system can
 * make. Falling back to the default would do exactly that: `marketng_list`
 * would return every counted registration with every column, which is the
 * artefact the caller was explicitly trying not to ask for.
 */
const PURPOSES: Readonly<Record<ExportPurposeId, ExportPurposeSpec>> = Object.freeze({
  participation_report: PARTICIPATION_REPORT,
  attendance_only: ATTENDANCE_ONLY,
  marketing_list: MARKETING_LIST,
  survey_free_text: SURVEY_FREE_TEXT,
  wheel_results: WHEEL_RESULTS,
});

export const EXPORT_PURPOSE_IDS = Object.freeze(
  Object.keys(PURPOSES) as ExportPurposeId[],
);

/**
 * The purpose an export defaults to when nothing names one.
 *
 * The safe default is the one that is not permission to contact anybody, and it
 * is also the one the export already produced, so an existing caller gets what
 * it always got. The dangerous artefact has to be asked for by name, which is
 * the whole of "impossible by accident".
 */
export const DEFAULT_EXPORT_PURPOSE: ExportPurposeId = "participation_report";

/** A purpose nobody defined. Its own class so a route can answer 400 rather than 500. */
export class ExportPurposeUnknown extends Error {
  constructor(asked: unknown) {
    super(
      `no export purpose called ${JSON.stringify(asked)}. Known purposes are ${EXPORT_PURPOSE_IDS.join(", ")}. ` +
        `Refused rather than defaulted: falling back would answer a misspelt request for a narrow file with the ` +
        `widest one this system can produce, which is the opposite of what the caller asked for.`,
    );
    this.name = "ExportPurposeUnknown";
  }
}

/** A field list this purpose cannot honour. Its own class for the same reason. */
export class ExportFieldsRefused extends Error {
  constructor(detail: string) {
    super(
      `refusing this field list: ${detail}. Refused rather than silently narrowed, because a caller who ` +
        `believes they asked for a column and did not get it reads the gap as "nobody consented" rather than ` +
        `as "you asked for something this template does not carry".`,
    );
    this.name = "ExportFieldsRefused";
  }
}

/**
 * No processing agreement on the client this activation belongs to.
 *
 * Handoff rule 12, enforced rather than stated. 126 put the agreement on the
 * client row and this is the refusal that gives it teeth: without one there is
 * no recorded lawful basis to hand a client a file of their attendees' details,
 * so no file is produced.
 *
 * ITS OWN CLASS AND NOT AccessDenied, which is the tempting reuse because both
 * end in a refusal. They are different refusals about different subjects and
 * conflating them would mislead exactly the person reading the message at three
 * in the morning. AccessDenied means *you* may not read this activation. This
 * means the caller's access is fine and the client's paperwork is not, which is
 * fixed by recording an agreement rather than by granting anybody anything.
 * routes.ts answers it 409 for that reason: not "who are you" but "not in this
 * state".
 *
 * A grandfathered client does NOT raise this. See 126 — every client alive when
 * that migration ran is permitted on that basis, and the marker stays on the
 * row so the ones still owing a signed document remain findable.
 */
export class ExportAgreementMissing extends Error {
  constructor(clientId: string) {
    super(
      `refusing to produce a file for client ${clientId}: no processing agreement is on file. ` +
        `Registration can still be set up and rehearsed; producing personal data is refused until the ` +
        `agreement is recorded, because a file handed over without one is a disclosure with no recorded ` +
        `lawful basis behind it and there is no way to take it back once it has been sent.`,
    );
    this.name = "ExportAgreementMissing";
  }
}

/**
 * The artefact does not match the purpose it claims. Never expected; asserted
 * anyway, because the whole value of this file is that it holds when the code
 * around it is wrong. See assertPurposeHonoured.
 */
export class ExportRuleViolated extends Error {
  constructor(detail: string) {
    super(
      `abandoning this export: ${detail}. The file was discarded rather than returned, because an export ` +
        `that does not match the purpose it was asked for is a disclosure nobody authorised and there is no ` +
        `way to take it back once it has been sent.`,
    );
    this.name = "ExportRuleViolated";
  }
}

export function purposeFor(id: string | undefined | null): ExportPurposeSpec {
  const chosen = id ?? DEFAULT_EXPORT_PURPOSE;
  const spec = Object.prototype.hasOwnProperty.call(PURPOSES, chosen)
    ? PURPOSES[chosen as ExportPurposeId]
    : undefined;
  if (spec === undefined) throw new ExportPurposeUnknown(id);
  return spec;
}

export type ResolvedShape = {
  purpose: ExportPurposeSpec;
  /** What the file will actually carry, in canonical order. */
  columns: readonly ExportColumn[];
  /** Whether the caller asked for fewer columns than the purpose permits. */
  minimised: boolean;
};

/**
 * Turn a purpose and an optional field list into the columns a file will carry.
 *
 * Pure, and separated from export.ts so that a route can refuse a malformed
 * request before anything is read — which matters because the alternative is a
 * `report_access` row recording an export that was never going to happen,
 * against an outcome vocabulary ('answered', 'refused', 'failed') that has no
 * word for "the caller asked for a column that does not exist".
 *
 * exportActivation calls this again rather than trusting a caller to have done
 * it. Duplicated deliberately: this is a library function that hands personal
 * data to whoever calls it, and the one thing it must not do is assume its
 * arguments have been through a validator it cannot see.
 */
export function resolveShape(
  purposeId: string | undefined | null,
  requested?: readonly string[] | undefined,
): ResolvedShape {
  const purpose = purposeFor(purposeId);

  if (requested === undefined) {
    return { purpose, columns: purpose.columns, minimised: false };
  }

  const asked = [...new Set(requested.map((f) => f.trim()).filter((f) => f.length > 0))];
  if (asked.length === 0) {
    throw new ExportFieldsRefused(
      "the field list was empty. An export of no columns is not a narrower export, it is a file with rows " +
        "and nothing in them, and it is far more likely to be a caller sending `fields=` by accident. Omit " +
        "the parameter to get everything this purpose carries",
    );
  }

  const unknown = asked.filter((f) => !(ALL_EXPORT_COLUMNS as readonly string[]).includes(f));
  if (unknown.length > 0) {
    throw new ExportFieldsRefused(
      `no export column is called ${unknown.map((f) => JSON.stringify(f)).join(", ")}. The columns are ` +
        ALL_EXPORT_COLUMNS.join(", "),
    );
  }

  const notCarried = asked.filter((f) => !(purpose.columns as readonly string[]).includes(f));
  if (notCarried.length > 0) {
    throw new ExportFieldsRefused(
      `the ${purpose.id} template does not carry ${notCarried.join(", ")}. It carries ` +
        `${purpose.columns.join(", ")}, and a field list cannot widen a purpose. Asking for a column a ` +
        `template withholds is asking for the wrong template`,
    );
  }

  // Union with the unminimisable set rather than intersection with the request,
  // so a caller who asks only for `contact_email` still gets the consent
  // account beside it. See UNMINIMISABLE_COLUMNS for why that is not a
  // convenience.
  const keep = new Set<string>([...asked, ...UNMINIMISABLE_COLUMNS]);
  const columns = purpose.columns.filter((c) => keep.has(c));

  return {
    purpose,
    columns,
    minimised: columns.length < purpose.columns.length,
  };
}

// --------------------------------------------------------------- the egress gate

/**
 * The state of one row's consents, as the row itself carries it.
 *
 * Deliberately the row's own `consent` map rather than anything the query
 * produced, because this is what the person reading the file will see. If the
 * two ever disagree, the file is what was disclosed.
 */
type RowLike = { consent?: ConsentStates | Record<string, string> | null } & Record<string, unknown>;

/** Whether one row satisfies every consent a purpose requires. */
export function rowSatisfies(spec: ExportPurposeSpec, row: RowLike): boolean {
  const consent = (row.consent ?? {}) as Record<string, string>;
  return spec.requires.every((purpose) => consent[purpose] === "granted");
}

/**
 * Refuse to hand back a file that does not match the purpose it was asked for.
 *
 * WHY THIS EXISTS WHEN THE CODE ABOVE ALREADY DOES IT. Because the code above
 * is where the mistake will be. Every failure this repository has recorded in
 * the report path was one line away from correct in a direction nothing
 * noticed: a viewer id set without the role dropped, an audit insert inside a
 * transaction that gets rolled back, an actor taken from a query string. In
 * each case the surface reported success. A gate that inspects the finished
 * artefact fails those cases loudly, because it is not asking whether the right
 * decision was made — it is asking what is actually in the file.
 *
 * IT READS THE SERIALISED FORM, and that is the part worth not simplifying. The
 * caller receives JSON, and an object graph and its JSON are not the same thing:
 * a getter, a `toJSON`, a non-enumerable property or a class instance can put a
 * value on the wire that is not on the object, or hide one that is. Checking the
 * object would be checking the thing that does not leave the building.
 *
 * It throws rather than filtering. Filtering would make this a second place
 * where the rule is implemented, and two implementations of one rule is how they
 * come to disagree; more importantly, a gate that quietly repairs its input
 * hides the bug it was written to surface, so the next export is wrong in the
 * same way and nobody hears about it.
 */
export function assertPurposeHonoured(
  shape: ResolvedShape,
  artefact: { rows: unknown[]; manifest: { row_count: number } },
): void {
  // THE REQUIRED SET IS NOT DERIVED FROM THE SHAPE, and that is the whole
  // difference between a gate and a restatement.
  //
  // It read `shape.columns.filter(isUnminimisable)` until a mutation test broke
  // resolveShape so that a field list could drop `consent`. The gate did notice
  // — by accident, through the mark-and-contents arm below, reporting `row 0 is
  // marked undefined` — because a shape that had lost `consent` also produced a
  // required set that had lost it, so the arm written for exactly that failure
  // never fired. A gate that takes its expectations from the code it is
  // checking inherits that code's mistakes, which is the one thing it exists
  // not to do. So this list is the constant, unconditionally, and `permitted`
  // is widened by the same set: if a shape somehow forbids a column that may
  // never be removed, the honest complaint is that the row has lost it rather
  // than that it is carrying it.
  const permitted = new Set<string>([...shape.columns, ...UNMINIMISABLE_COLUMNS]);
  const required = new Set<string>(UNMINIMISABLE_COLUMNS);

  // What leaves, rather than what was built. See the paragraph above.
  const onTheWire = JSON.parse(JSON.stringify(artefact)) as {
    rows: RowLike[];
    manifest: { row_count: number };
  };
  const rows = onTheWire.rows;

  if (rows.length !== onTheWire.manifest.row_count) {
    throw new ExportRuleViolated(
      `the manifest says ${onTheWire.manifest.row_count} rows and the file carries ${rows.length}. A count ` +
        `that disagrees with the file is the number a client reconciles against, so it is worse than no count`,
    );
  }

  for (const [index, row] of rows.entries()) {
    // Missing-before-stray, and the order is load bearing. A row that has lost
    // its consent account is the more serious of the two findings and is also
    // the one whose message a reader can act on, so it is reported first rather
    // than behind a complaint about some other column.
    for (const key of required) {
      if (!(key in row)) {
        throw new ExportRuleViolated(
          `row ${index} is missing ${key}, which no purpose and no field list may remove. A file whose rows ` +
            `have lost their account of what the person permitted is the flattened export this whole ` +
            `mechanism exists to prevent`,
        );
      }
    }

    for (const key of Object.keys(row)) {
      if (!permitted.has(key)) {
        throw new ExportRuleViolated(
          `row ${index} carries a column called ${JSON.stringify(key)}, which the ${shape.purpose.id} ` +
            `template does not permit. A column that reaches a file without passing through the purpose is a ` +
            `disclosure nobody chose`,
        );
      }
    }

    if (!rowSatisfies(shape.purpose, row)) {
      // No attendee_ref in the message and no field values. This string reaches
      // a log, and a log line naming who was nearly disclosed is a second
      // disclosure of the same person by the mechanism that stopped the first.
      throw new ExportRuleViolated(
        `row ${index} of a ${shape.purpose.id} does not hold a live consent for every one of ` +
          `${shape.purpose.requires.join(", ")}. This file is an instruction to contact people, so a row in ` +
          `it that should not be there is a mailing to somebody who declined`,
      );
    }

    if (row.personal_data !== "shared") {
      for (const field of PERSONAL_EXPORT_COLUMNS) {
        if (field in row && row[field] !== null) {
          throw new ExportRuleViolated(
            `row ${index} is marked ${JSON.stringify(row.personal_data)} and still carries a value in ` +
              `${field}. The mark and the contents have to agree, because the mark is what a reader trusts`,
          );
        }
      }
    }
  }
}
